import { useEffect, useState } from "react";
import { Disc3, Music2, Sparkles, Flame, Heart, ExternalLink, Plus } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import ShareButtons from "@/components/ShareButtons";
import ExclusiveDownloads from "@/components/ExclusiveDownloads";
import NewsletterSignup from "@/components/NewsletterSignup";
import { OVA40_COVER as VINYL, AUDIO_PORN_COVER, AUDIO_PORN_FRONT, OVA40_KIT, AUDIO_PORN_KIT, OVA40_KIT_URL, AUDIO_PORN_KIT_URL, ELASTICSTAGE_URL, OVA40_VINYL_URL } from "@/lib/assets";
import { SINGLES as SINGLES_FALLBACK } from "@/lib/aboutContent";

const VIBE =
  "https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBwdXJwbGUlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc3Mjg2NTh8MA&ixlib=rb-4.1.0&q=85";

const VIBE_META = {
  "The Flow": {
    icon: Flame,
    blurb: "Lyrical, Philly-style hip hop. Bars sharpened on subway tile and South Street brick.",
    accent: "var(--gold)",
  },
  "The Vibe": {
    icon: Sparkles,
    blurb: "Sultry, soul-touching R&B. Velvet for the late-night drive home.",
    accent: "var(--purple-accent)",
  },
  "The Spirit": {
    icon: Heart,
    blurb: "Faith-based, inspirational records. Including the timeless 'Simple Prayer.'",
    accent: "#f0d166",
  },
};

// Module-level guard so the Spotify-return toast fires exactly once
// (survives React StrictMode's dev double-invoke of effects).
let spotifyReturnHandled = false;

export default function Music() {
  const [tracks, setTracks] = useState([]);
  const [singles, setSingles] = useState(SINGLES_FALLBACK);

  useEffect(() => {
    api.get("/public/tracks").then((r) => setTracks(r.data.tracks || []));
    api
      .get("/public/singles")
      .then((r) => {
        const list = r.data.singles || [];
        if (list.length) setSingles(list);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (spotifyReturnHandled) return;
    const params = new URLSearchParams(window.location.search);
    const saved = params.get("spotify_saved");
    if (!saved) return;
    spotifyReturnHandled = true;
    // Defer so the global <Toaster> has mounted/subscribed (survives StrictMode).
    setTimeout(() => {
      if (saved === "1") {
        toast.success("Saved! 🎉 Audio Porn & OVA 40 are in your Spotify library.");
      } else if (saved === "denied") {
        toast("No worries — you can add them anytime.");
      } else {
        toast.error("Couldn't save to Spotify. Please try again.");
      }
    }, 350);
    params.delete("spotify_saved");
    const qs = params.toString();
    window.history.replaceState({}, "", `/music${qs ? `?${qs}` : ""}`);
  }, []);

  const flow = tracks.filter((t) => t.category === "The Flow");
  const vibe = tracks.filter((t) => t.category === "The Vibe");
  const spirit = tracks.filter((t) => t.category === "The Spirit");

  return (
    <div data-testid="music-page">
      {/* AUDIO PORN — UPCOMING RELEASE */}
      <section
        className="relative pt-28 pb-24 overflow-hidden"
        data-testid="audio-porn-upcoming"
      >
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${AUDIO_PORN_COVER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(60px) saturate(1.2)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,3,8,0.85) 0%, rgba(5,3,8,0.7) 50%, rgba(5,3,8,0.95) 100%)",
          }}
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-up">
            <SectionLabel number="Out Now" label="Official Music" />
            <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9] mt-3">
              Official Music by{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                Nina Capone
                <sup className="align-super text-[0.4em] tracking-normal opacity-80">™</sup>
              </em>
            </h2>
            <p className="text-white/60 text-base max-w-2xl leading-relaxed mt-4">
              Explore the official Nina Capone catalog, including{" "}
              <em className="italic">Ova 40</em>,{" "}
              <em className="italic">Audio Porn</em>, singles, videos, physical
              releases, and exclusive fan experiences. Stream, purchase, and
              experience music directly from Nina Capone™.
            </p>
          </div>
          <div className="flex items-center gap-3 mb-8">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] border"
              style={{
                color: "var(--gold)",
                borderColor: "rgba(212,175,55,0.5)",
                background: "rgba(212,175,55,0.05)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full live-dot"
                style={{ background: "var(--gold)" }}
              />
              Out Now
            </span>
            <span className="eyebrow">Mob Mistress Entertainment Presents</span>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 fade-up">
              <SpinningVinyl
                cover={AUDIO_PORN_FRONT}
                alt="Audio Porn album cover by Nina Capone"
                testid="audio-porn"
              />
              <div className="flex items-center justify-center gap-3 mt-8">
                <span className="px-2 py-1 text-[9px] font-bold tracking-wider bg-white text-black">
                  PARENTAL ADVISORY
                </span>
                <p className="eyebrow">Produced By Glitch</p>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-7">
              <div>
                <p className="font-serif italic text-base text-white/55 mb-2">
                  The next chapter
                </p>
                <h1 className="font-serif font-light tracking-tighter text-[3.5rem] sm:text-8xl lg:text-[8.5rem] leading-[0.85]">
                  Audio
                  <br />
                  <em
                    className="italic font-light"
                    style={{ color: "var(--gold)" }}
                  >
                    Porn.
                  </em>
                </h1>
              </div>

              <p
                className="font-serif italic text-xl sm:text-2xl lg:text-3xl tracking-tight font-light leading-[1.3] text-white/85 border-l-2 pl-5 sm:pl-6"
                style={{ borderColor: "var(--gold)" }}
              >
                &ldquo;Audio Porn isn&rsquo;t about sex.
                <br />
                It&rsquo;s about <span style={{ color: "var(--gold)" }}>intimacy with sound</span> — the kind that
                gets under your skin, in your chest, in your thoughts.&rdquo;
              </p>

              <div className="space-y-4 text-white/65 leading-relaxed text-base max-w-xl">
                <p>
                  The way vocals, riffs, and raw emotion can feel so intense
                  it almost seems… forbidden. I named this album <em className="italic">Audio Porn</em> because
                  of how it&rsquo;s delivered — unfiltered, immersive, and deeply
                  felt.
                </p>
                <p>
                  It&rsquo;s that private, almost secret experience you have when
                  music hits you in a way you can&rsquo;t explain… but you feel
                  everything. This is about connection, vulnerability, and
                  the power of sound to move you in ways words alone can&rsquo;t.
                </p>
                <p
                  className="text-xs uppercase tracking-[0.3em] pt-2"
                  style={{ color: "var(--gold)" }}
                >
                  — Nina Capone
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a
                  href={AUDIO_PORN_KIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="audio-porn-kit-cta"
                >
                  <Disc3 size={14} /> Buy the Collector Kit
                </a>
                <a
                  href={ELASTICSTAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="audio-porn-vinyl-cd-cta"
                >
                  <Disc3 size={14} /> Buy Vinyl + CD
                </a>
                <a
                  href="https://open.spotify.com/album/6JIR2LJXlvC6LateWdyxwv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="audio-porn-spotify-cta"
                >
                  <ExternalLink size={14} /> Listen On Spotify
                </a>
                <a
                  href="https://distrokid.com/hyperfollow/ninacapone/audio-porn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="audio-porn-hyperfollow"
                >
                  Save On Every DSP
                </a>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Share
                </span>
                <ShareButtons
                  shareUrl={
                    typeof window !== "undefined"
                      ? `${window.location.origin}/api/og/album/audio-porn`
                      : "/api/og/album/audio-porn"
                  }
                  canonicalUrl={
                    typeof window !== "undefined"
                      ? `${window.location.origin}/music`
                      : "/music"
                  }
                  text="Audio Porn — Nina Capone's new album. Out now on every DSP."
                  testidPrefix="audio-porn-share"
                  size="sm"
                />
              </div>
            </div>
          </div>

          <SpotifyTracklist
            albumId="6JIR2LJXlvC6LateWdyxwv"
            title="Audio Porn — Nina Capone"
            testid="audio-porn-tracklist"
          />
        </div>
      </section>

      {/* SPOTIFY FOLLOW STRIP */}
      <section
        className="relative border-y border-white/5 overflow-hidden"
        data-testid="spotify-follow-strip"
      >
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            background:
              "radial-gradient(circle at 20% 50%, #1DB954 0%, transparent 55%), radial-gradient(circle at 80% 50%, var(--gold) 0%, transparent 55%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-14 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-3">
              <div className="flex items-center gap-3">
                <span
                  className="inline-block w-2 h-2 rounded-full animate-pulse"
                  style={{ background: "#1DB954" }}
                />
                <span
                  className="eyebrow"
                  style={{ color: "var(--gold)" }}
                >
                  Never Miss A Drop
                </span>
              </div>
              <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
                Follow Nina on{" "}
                <em
                  className="italic font-light"
                  style={{ color: "#1DB954" }}
                >
                  Spotify
                </em>
                .
              </h2>
              <p className="text-white/60 text-base max-w-xl leading-relaxed pt-1">
                Be first on every release. Every OVA 40 cut, every Audio Porn
                preview, every late-night drop — straight to your library the
                moment it lands. No notifications. No noise. Just the music.
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col items-start sm:items-center lg:items-end gap-4">
              <a
                href="https://open.spotify.com/artist/1aAI3BGhCIfzsuqx5kLh6O?si=VzVyKl2sRPS_5wiKhTU8iQ"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="spotify-follow-cta"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_10px_40px_-10px_rgba(29,185,84,0.6)]"
                style={{
                  background: "#1DB954",
                  color: "#000",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12A12 12 0 0 0 12 0zm5.5 17.3c-.2.4-.7.5-1 .3-2.8-1.7-6.3-2.1-10.5-1.1-.4.1-.8-.2-.9-.5-.1-.4.2-.8.5-.9 4.5-1 8.4-.6 11.5 1.3.4.2.5.6.3 1zm1.5-3.3c-.3.4-.8.6-1.2.3-3.2-2-8.1-2.5-11.9-1.4-.5.1-1-.1-1.1-.6-.1-.5.1-1 .6-1.1 4.4-1.3 9.8-.7 13.5 1.6.3.2.4.8.1 1.2zm.1-3.4C15.4 8.4 8.8 8.2 5.2 9.3c-.6.2-1.2-.2-1.4-.7-.2-.6.2-1.2.7-1.4 4.2-1.3 11.5-1 15.9 1.6.5.3.7 1 .4 1.5-.3.4-1 .6-1.5.3z" />
                </svg>
                Follow On Spotify
              </a>
              <a
                href={`${API}/spotify/save-albums`}
                data-testid="spotify-save-albums-cta"
                className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-medium text-sm tracking-wide border transition-all duration-300 hover:scale-[1.03]"
                style={{
                  borderColor: "#1DB954",
                  color: "#1DB954",
                  background: "rgba(29,185,84,0.06)",
                }}
              >
                <Plus size={17} strokeWidth={2.4} />
                Add Both Albums To Your Library
              </a>
              <span className="text-[11px] tracking-[0.2em] uppercase text-white/40">
                Join The Capone Camp
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* OVA 40 FEATURE */}
      <section
        id="ova-40"
        className="relative py-28 overflow-hidden scroll-mt-24"
        data-testid="ova40-section"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${VIBE})`,
            backgroundSize: "cover",
          }}
        />
        <div className="absolute inset-0 bg-[#050308]/85" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6 fade-up relative z-10">
              <div className="flex items-center gap-3">
                <SectionLabel number="Premier Release" label="Debut Album" />
                <span
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.25em] uppercase border"
                  style={{
                    color: "var(--gold)",
                    borderColor: "rgba(232,180,184,0.45)",
                    background: "rgba(232,180,184,0.08)",
                  }}
                  data-testid="ova40-out-now-badge"
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full live-dot"
                    style={{ background: "var(--gold)" }}
                  />
                  Out Now
                </span>
              </div>
              <h1 className="font-serif font-light tracking-tighter text-6xl sm:text-7xl lg:text-9xl leading-[0.85]">
                OVA{" "}
                <em
                  className="italic font-light"
                  style={{ color: "var(--gold)" }}
                >
                  40.
                </em>
              </h1>
              <p className="text-white/60 leading-relaxed max-w-md">
                Seventeen tracks. Three decades of work compressed into one
                full-length statement. OVA 40 is Nina&rsquo;s official debut album —
                a meditation on grown-woman authority across hip hop, R&B,
                and faith.
              </p>
              <div className="flex flex-wrap gap-3 text-xs text-white/50 pt-2">
                <Badge>17 Tracks</Badge>
                <Badge>Independent Release</Badge>
                <Badge>Mastered In Philly</Badge>
              </div>
              <div className="flex flex-wrap gap-3 pt-4">
                <a
                  href={OVA40_KIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="ova40-kit-cta"
                >
                  <Disc3 size={14} /> Buy the Collector Kit
                </a>
                <a
                  href={OVA40_VINYL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="ova40-vinyl-cd-cta"
                >
                  <Disc3 size={14} /> Buy Vinyl + CD
                </a>
                <a
                  href="https://open.spotify.com/album/2XDRxKesvCeCc6oS4dfX3g?si=O4VMDHl0T_K3rdG3-oHpfA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="ova40-spotify-cta"
                >
                  <ExternalLink size={14} /> Listen On Spotify
                </a>
                <a
                  href="https://distrokid.com/hyperfollow/ninacapone/ova-40"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                  data-testid="ova40-hyperfollow"
                >
                  Save On Every DSP
                </a>
              </div>
              <p
                className="text-[10px] uppercase tracking-[0.25em] pt-1"
                style={{ color: "var(--gold)" }}
                data-testid="ova40-vinyl-dates"
              >
                Out Now · Vinyl, CD &amp; Collector Kit Available
              </p>
              <div className="flex items-center gap-3 pt-3">
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                  Share
                </span>
                <ShareButtons
                  shareUrl={
                    typeof window !== "undefined"
                      ? `${window.location.origin}/api/og/album/ova-40`
                      : "/api/og/album/ova-40"
                  }
                  canonicalUrl={
                    typeof window !== "undefined"
                      ? `${window.location.origin}/music`
                      : "/music"
                  }
                  text="OVA 40 — Nina Capone's 17-track debut album. Out now on every DSP."
                  testidPrefix="ova40-share"
                  size="sm"
                />
              </div>
            </div>

            <div className="lg:col-span-7 relative">
              <SpinningVinyl
                cover={VINYL}
                alt="OVA 40 — I'm Over Everything · Nina Capone"
                testid="ova40"
              />

              <div className="flex items-center justify-center gap-3 mt-8">
                <span className="px-2 py-1 text-[9px] font-bold tracking-wider bg-white text-black">
                  PARENTAL ADVISORY
                </span>
                <p className="eyebrow">I&rsquo;m Over Everything</p>
              </div>
            </div>
          </div>

          <SpotifyTracklist
            albumId="2XDRxKesvCeCc6oS4dfX3g"
            title="OVA 40 — Nina Capone"
            testid="ova40-tracklist"
          />
        </div>
      </section>

      {/* COLLECTOR KITS — PHYSICAL */}
      <section className="py-24 border-t border-white/5" data-testid="collector-kits">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <SectionLabel label="Physical · Own It" />
            <h3 className="font-serif text-4xl lg:text-6xl tracking-tighter font-light mt-3 leading-[0.92]">
              The Collector{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                Kits.
              </em>
            </h3>
            <p className="text-white/60 text-base max-w-2xl mx-auto leading-relaxed mt-5">
              Limited-edition physical kits for both albums — vinyl, CDs, signed
              inserts, and exclusive collectible extras. Multiple purchase
              options and bundles available.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                img: OVA40_KIT,
                name: "OVA 40",
                genre: "Rap Album · Collector Kit",
                url: OVA40_KIT_URL,
                testid: "kit-ova40",
              },
              {
                img: AUDIO_PORN_KIT,
                name: "Audio Porn",
                genre: "R&B Album · Collector Kit",
                url: AUDIO_PORN_KIT_URL,
                testid: "kit-audioporn",
              },
            ].map((k) => (
              <div key={k.name} className="surface p-6 flex flex-col" data-testid={k.testid}>
                <a href={k.url} target="_blank" rel="noopener noreferrer" className="group block">
                  <div className="relative rounded-lg overflow-hidden ring-1 ring-white/10 bg-black/30">
                    <img
                      src={k.img}
                      alt={`${k.name} collector kit — vinyl, CD and physical extras`}
                      loading="lazy"
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                  </div>
                </a>
                <div className="flex items-end justify-between gap-4 mt-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                      {k.genre}
                    </p>
                    <h4 className="font-serif text-3xl tracking-tighter font-light mt-1">
                      {k.name}
                    </h4>
                  </div>
                  <a
                    href={k.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-xs shrink-0"
                    data-testid={`${k.testid}-buy`}
                  >
                    <Disc3 size={14} /> Shop the Kit
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 space-y-4">
            <a
              href={ELASTICSTAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-gold inline-flex items-center gap-2 px-8 py-3.5 text-xs"
              data-testid="kits-vinyl-cd-store"
            >
              <Disc3 size={14} /> Prefer just Vinyl &amp; CDs? Shop physical releases
            </a>
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/35">
              Secure checkout via Kit &amp; elasticstage · Ships worldwide
            </p>
          </div>
        </div>
      </section>

      {/* STREAM EVERYWHERE */}
      <section className="py-20 border-t border-white/5" data-testid="stream-platforms">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 mb-10">
            <div>
              <SectionLabel label="Listen · Follow" />
              <h3 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light mt-2">
                Nina on every <em className="italic" style={{ color: "var(--gold)" }}>platform</em>.
              </h3>
              <p className="text-sm text-white/55 mt-3 max-w-xl leading-relaxed">
                Tap your platform. Follow. Done. New drops auto-deliver to your library.
              </p>
            </div>
            <a
              href="https://distrokid.com/hyperfollow/ninacapone/ova-40"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
              data-testid="hyperfollow-cta"
            >
              Pre-Save & Stream
            </a>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              {
                id: "spotify",
                label: "Spotify",
                tag: "Follow Artist",
                href: "https://open.spotify.com/artist/1aAI3BGhCIfzsuqx5kLh6O?si=VzVyKl2sRPS_5wiKhTU8iQ",
                color: "#1DB954",
                Icon: (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24Zm5.5 17.3a.75.75 0 0 1-1.03.26c-2.83-1.73-6.4-2.12-10.6-1.16a.75.75 0 1 1-.34-1.46c4.6-1.05 8.55-.6 11.7 1.32.36.22.47.69.27 1.04Zm1.46-3.16a.94.94 0 0 1-1.29.32c-3.24-1.99-8.18-2.57-12-1.41a.94.94 0 0 1-.55-1.8c4.35-1.32 9.78-.68 13.5 1.61.45.27.59.86.34 1.28Zm.13-3.3C15.4 8.55 8.9 8.27 5.32 9.4a1.13 1.13 0 1 1-.66-2.16c4.13-1.27 11.27-.94 15.66 1.66a1.13 1.13 0 0 1-1.18 1.94Z" />
                  </svg>
                ),
              },
              {
                id: "apple",
                label: "Apple Music",
                tag: "Add Artist",
                href: "https://music.apple.com/us/artist/nina-capone/296313757",
                color: "#FA2D48",
                Icon: (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M19.5 5.5C19.5 3.6 18 2 16 2H8C6 2 4.5 3.6 4.5 5.5v13C4.5 20.4 6 22 8 22h8c2 0 3.5-1.6 3.5-3.5v-13Zm-4 9.9c0 1.4-1 2.4-2.3 2.4-1 0-1.8-.7-1.8-1.7s.8-1.7 2-1.7c.5 0 .9.1 1.2.2V9.4l-5 1v6.7c0 1.4-1 2.4-2.3 2.4-1 0-1.8-.7-1.8-1.7s.8-1.7 2-1.7c.5 0 .9.1 1.2.2V8.4c0-.5.4-.9.9-1L15 6c.3 0 .5.2.5.5v9Z" />
                  </svg>
                ),
              },
              {
                id: "youtube",
                label: "YouTube",
                tag: "Subscribe",
                href: "https://www.youtube.com/@indastreetsradio/releases",
                color: "#FF0033",
                Icon: (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M23.5 6.5a3 3 0 0 0-2.1-2.1C19.5 4 12 4 12 4s-7.5 0-9.4.4A3 3 0 0 0 .5 6.5C.1 8.4.1 12 .1 12s0 3.6.4 5.5a3 3 0 0 0 2.1 2.1C4.5 20 12 20 12 20s7.5 0 9.4-.4a3 3 0 0 0 2.1-2.1c.4-1.9.4-5.5.4-5.5s0-3.6-.4-5.5ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z" />
                  </svg>
                ),
              },
              {
                id: "reverbnation",
                label: "Reverbnation",
                tag: "Follow",
                href: "https://www.reverbnation.com/ninacapone",
                color: "#0E76E5",
                Icon: (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M12 2 2 8.5 12 15l10-6.5L12 2Zm0 13.5L2 9v7l10 6.5L22 16V9l-10 6.5Z" />
                  </svg>
                ),
              },
              {
                id: "n1m",
                label: "N1M",
                tag: "Follow",
                href: "https://www.n1m.com/ninacapone",
                color: "#E8B4B8",
                Icon: (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 14a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm0-5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z" />
                  </svg>
                ),
              },
            ].map((p) => (
              <a
                key={p.id}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${p.label} — ${p.tag}`}
                className="group relative surface p-5 flex flex-col gap-4 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-transparent"
                style={{
                  ["--platform-color"]: p.color,
                }}
                data-testid={`platform-${p.id}`}
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    background: `radial-gradient(120% 80% at 0% 0%, ${p.color}26, transparent 60%)`,
                    boxShadow: `inset 0 0 0 1px ${p.color}80`,
                  }}
                />
                <span
                  className="relative inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/10 group-hover:border-transparent transition-colors"
                  style={{ color: p.color }}
                >
                  {p.Icon}
                </span>
                <div className="relative">
                  <div className="font-serif text-xl tracking-tighter font-light group-hover:text-white transition-colors">
                    {p.label}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 text-[10px] uppercase tracking-[0.22em] text-white/45 group-hover:text-white/85 transition-colors">
                    {p.tag}
                    <ExternalLink size={10} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <ExclusiveDownloads variant="teaser" />

      {/* THREE VIBES */}
      <section className="py-28 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-6">
              <SectionLabel number="Discography" label="Three Vibes" />
              <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light">
                One artist.
                <br />
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Three frequencies.
                </em>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-white/60 leading-relaxed">
                Nina&rsquo;s broader catalog moves between three sounds — each one a
                different room of the same house.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <VibeCard title="The Flow" tracks={flow} />
            <VibeCard title="The Vibe" tracks={vibe} />
            <VibeCard title="The Spirit" tracks={spirit} />
          </div>

          {/* SIMPLE PRAYER — INSPIRATIONAL SPOTLIGHT */}
          <div
            className="mt-14 surface relative overflow-hidden p-10 lg:p-14"
            data-testid="simple-prayer-spotlight"
          >
            <div
              className="absolute inset-0 pointer-events-none opacity-60"
              style={{
                background:
                  "radial-gradient(ellipse 50% 80% at 100% 0%, rgba(212,175,55,0.18), transparent 60%), radial-gradient(ellipse 60% 80% at 0% 100%, rgba(74,14,78,0.45), transparent 60%)",
              }}
            />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-6">
                <div className="flex items-center gap-3">
                  <Heart size={18} style={{ color: "var(--gold)" }} fill="var(--gold)" fillOpacity={0.2} />
                  <p className="eyebrow">Inspirational Spotlight</p>
                </div>
                <h3 className="font-serif text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
                  Simple{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    Prayer.
                  </em>
                </h3>
                <p className="font-serif italic text-xl lg:text-2xl text-white/80 leading-relaxed max-w-2xl">
                  &ldquo;An open letter to the divine — written for anyone who&rsquo;s
                  ever needed quiet to hear themselves again.&rdquo;
                </p>
                <p className="text-sm text-white/55 max-w-xl leading-relaxed">
                  The faith-based standout from Nina&rsquo;s catalog, and the most
                  requested song at her speaking engagements. Streaming now
                  on Spotify as a dedicated single release.
                </p>
                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <a
                    href="https://open.spotify.com/album/63fcUPEfDWlX86rXDTwpcU?si=rotFPFRVQ_merdTHWoTObA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold inline-flex items-center gap-3 px-7 py-3 text-xs"
                    data-testid="simple-prayer-spotify"
                  >
                    <ExternalLink size={14} /> Listen On Spotify
                  </a>
                  <span className="text-xs uppercase tracking-[0.25em] text-white/45">
                    Faith · Inspirational · Dedicated Single
                  </span>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div
                  className="relative rounded-xl overflow-hidden"
                  style={{
                    boxShadow:
                      "0 30px 80px -30px rgba(212,175,55,0.25), 0 0 0 1px rgba(232,180,184,0.15)",
                  }}
                  data-testid="simple-prayer-embed"
                >
                  <iframe
                    title="Simple Prayer — Nina Capone"
                    src="https://open.spotify.com/embed/album/63fcUPEfDWlX86rXDTwpcU?utm_source=generator&theme=0"
                    width="100%"
                    height="380"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    style={{ display: "block", border: 0 }}
                  />
                </div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/35 mt-4 text-center">
                  Tap play · Streaming powered by Spotify
                </p>
              </div>
            </div>
          </div>

          {/* DAYS LIKE THIS — EMBED */}
          <div
            className="mt-10 surface p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-center"
            data-testid="days-like-this-spotlight"
          >
            <div className="lg:col-span-5 space-y-4">
              <div className="flex items-center gap-3">
                <Sparkles size={18} style={{ color: "var(--gold)" }} />
                <p className="eyebrow">Now Spinning · The Spirit</p>
              </div>
              <h3 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light leading-[0.95]">
                Days{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Like This.
                </em>
              </h3>
              <p className="text-sm text-white/60 leading-relaxed max-w-md">
                Faith meets the everyday grind. A track for the mornings when
                the work is quiet but the call is loud. Press play.
              </p>
              <a
                href="https://open.spotify.com/track/1h07Grw8QowomZRod7Mjun?si=33e937f360534e89"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
                style={{ color: "var(--gold)" }}
                data-testid="days-like-this-spotify"
              >
                <ExternalLink size={14} /> Open In Spotify
              </a>
            </div>
            <div className="lg:col-span-7">
              <iframe
                title="Days Like This — Nina Capone"
                src="https://open.spotify.com/embed/track/1h07Grw8QowomZRod7Mjun?utm_source=generator&theme=0"
                width="100%"
                height="152"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ border: 0, borderRadius: 12 }}
                data-testid="days-like-this-embed"
              />
            </div>
          </div>

          {/* ALL SINGLES */}
          <div className="mt-16" data-testid="singles-section">
            <div className="flex items-center gap-3 mb-2">
              <Music2 size={18} style={{ color: "var(--gold)" }} />
              <p className="eyebrow">The Singles</p>
            </div>
            <h3 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light leading-[0.95] mb-3">
              Every{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                single.
              </em>
            </h3>
            <p className="text-sm text-white/55 max-w-xl leading-relaxed mb-8">
              Nina&rsquo;s full run of independent single releases — tap any title
              to listen on Apple Music.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {singles.map((s) => (
                <a
                  key={s.title}
                  href={s.apple}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="surface p-3 flex items-center gap-4 group hover:border-[color:var(--gold)]/40 transition-all"
                  data-testid="music-single"
                >
                  <div className="relative shrink-0 w-16 h-16 rounded-md overflow-hidden ring-1 ring-white/10">
                    <img
                      src={s.art}
                      alt={`${s.title} cover art`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ExternalLink size={16} style={{ color: "var(--gold)" }} />
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-white/85 group-hover:text-[color:var(--gold)] transition-colors truncate">
                      {s.title}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/35 mt-1">
                      {s.year} · Apple Music
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <NewsletterSignup source="music-page" testId="music-newsletter-signup" />
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center px-3 py-1 border border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/60">
      {children}
    </span>
  );
}

function VibeCard({ title, tracks }) {
  const meta = VIBE_META[title];
  const Icon = meta?.icon || Music2;
  return (
    <div
      className="surface p-8 lg:p-10 relative overflow-hidden"
      data-testid={`vibe-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-6">
        <Icon size={22} style={{ color: meta?.accent }} />
        <p className="eyebrow">{title}</p>
      </div>
      <h3 className="font-serif text-4xl tracking-tighter font-light mb-3">
        {title}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed mb-8 min-h-[3rem]">
        {meta?.blurb}
      </p>
      <ul className="space-y-3 border-t border-white/5 pt-6">
        {tracks.map((t) => (
          <li key={t.id}>
            <a
              href={t.spotify_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-between text-sm group ${
                t.featured
                  ? "py-3 px-3 -mx-3 border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/[0.04]"
                  : ""
              }`}
              data-testid={t.featured ? `featured-track-${t.title.toLowerCase().replace(/\s+/g, "-")}` : undefined}
            >
              <span className="flex items-center gap-3 flex-1 min-w-0">
                {t.featured && (
                  <Sparkles
                    size={14}
                    style={{ color: "var(--gold)" }}
                    className="shrink-0"
                  />
                )}
                <span
                  className={`font-serif italic transition-colors ${
                    t.featured
                      ? "text-[color:var(--gold)] text-base"
                      : "text-white/85 group-hover:text-[color:var(--gold)]"
                  }`}
                >
                  {t.title}
                </span>
                {t.featured && (
                  <span className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--gold)]/80 border border-[color:var(--gold)]/40 px-2 py-0.5">
                    Featured
                  </span>
                )}
              </span>
              <span
                className={`text-xs font-mono ${
                  t.featured
                    ? "text-[color:var(--gold)]"
                    : "text-white/40 group-hover:text-[color:var(--gold)]"
                }`}
              >
                {t.duration}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}


// Reusable spinning-vinyl record with the album cover as the center label.
function SpinningVinyl({ cover, alt, testid }) {
  return (
    <div
      className="relative aspect-square max-w-[480px] mx-auto"
      data-testid={`${testid}-vinyl`}
    >
      {/* Vinyl record assembly — spins as one unit */}
      <div className="absolute inset-0 spin-slow">
        {/* Outer disc with concentric grooves */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: `
              radial-gradient(circle at 35% 30%, rgba(255,255,255,0.06) 0%, transparent 25%),
              repeating-radial-gradient(circle at center, #0d0509 0, #0d0509 1px, #1a0d14 1px, #1a0d14 2px),
              radial-gradient(circle at center, #1a0d14 0%, #050308 65%, #02010a 100%)
            `,
            boxShadow:
              "0 35px 90px rgba(0,0,0,0.7), inset 0 0 80px rgba(232,180,184,0.06), 0 0 0 1px rgba(232,180,184,0.12)",
          }}
        />
        {/* Subtle rose gold groove rings */}
        <div
          className="absolute inset-[6%] rounded-full pointer-events-none"
          style={{ border: "1px solid rgba(232,180,184,0.12)" }}
        />
        <div
          className="absolute inset-[12%] rounded-full pointer-events-none"
          style={{ border: "1px solid rgba(232,180,184,0.08)" }}
        />
        <div
          className="absolute inset-[18%] rounded-full pointer-events-none"
          style={{ border: "1px solid rgba(232,180,184,0.18)" }}
        />

        {/* Album cover acting as the center label */}
        <div
          className="absolute inset-[22%] rounded-full overflow-hidden"
          style={{
            boxShadow:
              "0 0 0 1px rgba(232,180,184,0.4), 0 0 30px rgba(232,180,184,0.18), inset 0 0 20px rgba(0,0,0,0.4)",
          }}
        >
          <img
            src={cover}
            alt={alt}
            className="w-full h-full object-cover"
            data-testid={`${testid}-album-cover`}
          />
        </div>

        {/* Center spindle */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[3.5%] aspect-square rounded-full"
          style={{
            background:
              "radial-gradient(circle at 35% 35%, #f3c8cb, #b76e79 60%, #050308 100%)",
            boxShadow: "0 0 8px rgba(0,0,0,0.6)",
          }}
        />
      </div>

      {/* Reflective highlight (does not spin) */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 28% 22%, rgba(255,255,255,0.07), transparent 55%)",
        }}
      />
    </div>
  );
}

// Full, always-accurate album tracklist powered by a Spotify embed.
function SpotifyTracklist({ albumId, title, testid }) {
  return (
    <div className="mt-16 surface p-5 sm:p-8 lg:p-10" data-testid={testid}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="eyebrow">Full Tracklist</p>
          <h3 className="font-serif text-2xl sm:text-3xl tracking-tighter font-light mt-1">
            Press play — every track
          </h3>
        </div>
        <a
          href={`https://open.spotify.com/album/${albumId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
          style={{ color: "#1DB954" }}
          data-testid={`${testid}-open`}
        >
          <ExternalLink size={14} /> Open In Spotify
        </a>
      </div>
      <div
        className="rounded-xl overflow-hidden"
        style={{ boxShadow: "0 0 0 1px rgba(232,180,184,0.12)" }}
      >
        <iframe
          title={title}
          src={`https://open.spotify.com/embed/album/${albumId}?utm_source=generator&theme=0`}
          width="100%"
          height="520"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{ display: "block", border: 0 }}
          data-testid={`${testid}-embed`}
        />
      </div>
    </div>
  );
}
